// config/handsontable.ts
import { parse, isValid, addMinutes, subMinutes } from 'date-fns';
import Handsontable from 'handsontable';
import { TABLE_HEIGHT, MIN_SPARE_ROWS } from './constants';
import { Shipment } from '../types';
import { formatInTimeZone } from 'date-fns-tz';

function getOffsetForField(shipmentData: Shipment | null, fieldName: string): number | undefined {
  if (!shipmentData?.timeOffsets) return undefined;

  if (fieldName === 'etd' || fieldName === 'atd') {
    return shipmentData.timeOffsets.etd || shipmentData.timeOffsets.atd;
  }
  if (fieldName === 'eta' || fieldName === 'ata') {
    return shipmentData.timeOffsets.eta || shipmentData.timeOffsets.ata;
  }
  return undefined;
}

function utcToLocalOffset(date: Date, offsetMinutes: number | undefined): Date {
  if (!offsetMinutes) return date;

  return addMinutes(date, offsetMinutes);
}

export interface EditorContext {
  instance: any;
  shipmentsMap?: Record<string, Shipment>;
}

// Factory function to create ISODateEditor with context
export function createISODateEditor(context: EditorContext) {
  return class ISODateEditorWithContext extends Handsontable.editors.DateEditor {
    private parsedDate: Date | null = null; // Store the parsed date

    private getFieldOffset(): number | undefined {
      const shipmentId = context.instance?.getDataAtRowProp(this.row, 'id');
      const shipmentData = context.shipmentsMap?.[shipmentId] ?? null;
      return getOffsetForField(shipmentData, this.prop as string);
    }

    private buildOffsetString(offsetMinutes: number | undefined): string {
      if (!offsetMinutes) return 'Z';
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const hours = Math.floor(Math.abs(offsetMinutes) / 60);
      const minutes = Math.abs(offsetMinutes) % 60;
      return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    getValue() {
      const dateValue = super.getValue();

      if (!dateValue || dateValue === '') {
        return null;
      }

      const offset = this.getFieldOffset();
      const offsetString = this.buildOffsetString(offset);

      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) {
          return null;
        }
        // Build ISO string: 2025-10-23T00:00:00+08:00
        const year = dateValue.getFullYear();
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00${offsetString}`;
      }

      if (typeof dateValue === 'string') {
        // Try parsing different formats
        const currentDate = parse(dateValue, 'dd/MM/yyyy', new Date());

        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00${offsetString}`;
      }

      return null;
    }

    setValue(newValue: string) {
      if (!newValue) {
        this.parsedDate = null;
        super.setValue('');
        return;
      }

      if (newValue && newValue.includes('T')) {
        // Parse the ISO date and adjust for the location offset
        const offset = this.getFieldOffset();
        const date = new Date(newValue);

        if (isValid(date)) {
          const offsetString = this.buildOffsetString(offset);
          const dateOnly = formatInTimeZone(newValue, offsetString, 'dd/MM/yyyy');
          this.parsedDate = parse(dateOnly, 'dd/MM/yyyy', new Date());
          super.setValue(dateOnly);
          return;
        }

        // Fallback: just use the date part
        const dateParts = newValue.split('T')[0];
        const dateOnly = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
        this.parsedDate = parse(dateOnly, 'dd/MM/yyyy', new Date());
        super.setValue(dateOnly);
        return;
      }

      this.parsedDate = parse(newValue, 'dd/MM/yyyy', new Date());
      super.setValue(newValue);
    }

    open() {
      super.open();

      // After opening, set Pikaday's date explicitly if we have a parsed date
      if (this.parsedDate && isValid(this.parsedDate) && (this as any).datePicker) {
        const picker = (this as any).$datePicker;
        if (picker) {
          picker.setDate(this.parsedDate, true); // true = silent (don't trigger onSelect)
        }
      }
    }
  };
}

// Custom ISO Date Editor
export class ISODateEditor extends Handsontable.editors.DateEditor {
  getValue() {
    const dateValue = super.getValue();

    if (!dateValue || dateValue === '') {
      return null;
    }

    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) {
        console.warn('Invalid Date object:', dateValue);
        return null;
      }
      return dateValue.toISOString();
    }

    if (typeof dateValue === 'string') {
      let parsedDate = parse(dateValue, 'dd/MM/yyyy', new Date());
      if (isValid(parsedDate)) {
        return parsedDate.toISOString();
      }

      parsedDate = parse(dateValue, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return parsedDate.toISOString();
      }

      parsedDate = new Date(dateValue);
      if (isValid(parsedDate)) {
        return parsedDate.toISOString();
      }

      console.warn('Could not parse date string:', dateValue);
      return null;
    }

    console.warn('Unexpected date value type:', typeof dateValue, dateValue);
    return null;
  }

  setValue(newValue: string) {
    if (!newValue) {
      super.setValue('');
      return;
    }

    if (newValue && newValue.includes('T')) {
      const dateOnly = newValue.split('T')[0];
      super.setValue(dateOnly);
      return;
    }

    super.setValue(newValue);
  }
}

// Register custom elements
export function registerCustomElements() {
  Handsontable.editors.registerEditor('iso-date', ISODateEditor);
}

// Base table settings
export function getBaseTableSettings() {
  return {
    rowHeaders: true,
    colHeaders: true,
    height: TABLE_HEIGHT,
    width: '100%',
    stretchH: 'all',
    manualColumnResize: true,
    themeName: 'ht-theme-main',
    search: true,
    className: 'htCenter htMiddle',
    licenseKey: 'non-commercial-and-evaluation',
    minSpareRows: MIN_SPARE_ROWS,
    hiddenColumns: {
      indicators: true,
    },
    autoWrapRow: false,
    wordWrap: false,
  } as const;
}

// Context menu configuration
export function getContextMenuConfig(
  setSelectedRow: (row: any) => void,
  setSidebarOpened: (opened: boolean) => void,
  handleDeleteShipment?: (shipmentId: string, reference: string) => void,
  shipmentData?: any[],
  projectId?: string,
) {
  return {
    items: {
      row_above: {},
      row_below: {},
      remove_row: {
        name: 'Remove row',
        callback: async function (this: any, key: any, selection: any) {
          try {
            const row = selection[0].start.row;
            if (!shipmentData) {
              alert('Cannot delete: shipment data not loaded');
              return;
            }
            const shipment = shipmentData[row];
            const reference = shipment.customFields?.internalReference || shipment.id;

            await handleDeleteShipment?.(shipment.id, reference);
          } catch (error) {
            console.error('Failed to delete shipment:', error);
            alert('Failed to delete shipment. Please try again.');
            return;
          }
        },
        disabled: function (this: any) {
          const selection = this.getSelected();
          if (!selection || selection.length === 0) return true;

          const row = selection[0][0];
          const shipment = shipmentData && shipmentData[row];

          return !shipment || !shipment.id;
        },
      },
      undo: {},
      redo: {},
      custom_action_1: {
        name: 'Show Details',
        callback: function (this: any, key: any, selection: any) {
          const row = selection[0].start.row;
          const rowData = this.getDataAtRow(row);
          setSelectedRow({ rowData, index: row });
          setSidebarOpened(true);
        },
      },
    },
  };
}
